import React, { useEffect, useState } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes } from '@bloomberg/media-ui-core';
import { PlusStroke } from '@bloomberg/media-ui-icons';
import {
  Lines,
  SecurityPill,
  Size,
  Variant,
} from '@bloomberg/media-ui-markets';

import { MARKET_DATA_MENU_ID } from '../constants/constants';
import { localizeUrl } from '../i18n';
import { useNavContext } from '../Nav/NavContext';
import { SubmenuDesktop } from '../SubmenuDesktop/index';
import { DropdownListItem, SubmenuItem, THEMES } from '../types/index';
import {
  TranslatedMessage,
  useTranslateMessage,
} from '../utils/translate-messages';

import styles from './MarketDataDropdown.module.css';

export type PricingData = {
  dayRange?: [string, string];
  highPrice?: number;
  id: string;
  label?: string;
  name: string;
  percentChange1Day: number;
  priceChange1Day?: number;
  price: string;
  shortName?: string;
  type?: string;
};

export type MarketDataDropdownProps = GlobalProps & {
  desktopBreakpointClassName?: string;
  marketDataTickersUrl?: string;
  theme?: THEMES;
  marketDataLinks: SubmenuItem;
  marketDataNews: SubmenuItem;
  tickers: DropdownListItem[];
  initialPricingData?: PricingData[];
  title?: string;
  trackingFunction: (action: string, eventLabel: string) => void;
};

const TICKER_COUNT = 12;

export const MarketDataDropdown = React.forwardRef<
  HTMLDivElement,
  MarketDataDropdownProps
>(function MarketDataDropdownComponent(props: MarketDataDropdownProps, ref) {
  const translateMessage = useTranslateMessage();
  const { locale } = useNavContext();
  const {
    desktopBreakpointClassName,
    marketDataLinks,
    marketDataNews,
    marketDataTickersUrl = 'https://www.bloomberg.com/bazaar/api/quotes-price-data',
    tickers,
    initialPricingData,
    theme = THEMES.DARK,
    title = translateMessage({
      id: 'media-ui-nav_topSecurities',
      defaultMessage: 'Top Securities',
    }),
    trackingFunction,
  } = props;

  const [pricingData, setPricingData] = useState<PricingData[] | null>(
    initialPricingData || null
  );

  const fetchTickersData = async () => {
    try {
      const url = `${marketDataTickersUrl}?tickers=${tickers
        .map(({ id }) => id)
        .join(',')}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Error parsing json for tickers from url: ${url}`);
      }
      const tickersWithDataJson = await response.json();

      const tickersWithLabel = tickersWithDataJson.map((data: PricingData) => {
        const label = tickers.find(({ id }) => data.id === id)?.label;

        return { ...data, label };
      });

      setPricingData(tickersWithLabel);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`Error fetching market data from tickers: ${err}`);
    }
  };

  useEffect(() => {
    fetchTickersData();
  }, []);

  const cx = classes([
    desktopBreakpointClassName,
    styles.marketDataDropdownContainer,
    {
      [styles.light]: theme === THEMES.LIGHT,
      [styles.dark]: theme === THEMES.DARK,
      [styles.blue]: theme === THEMES.BLUE,
    },
  ]);

  const menuItemTrackingFunction = (
    submenuText: string,
    dropdownListItemText: string
  ) => {
    trackingFunction(
      'click',
      `${MARKET_DATA_MENU_ID}-${submenuText}-${dropdownListItemText}`
    );
  };

  const marketDataTrackingFunction = (
    submenuText: string,
    securityPillLabel: string
  ) => {
    trackingFunction(
      'click',
      `${MARKET_DATA_MENU_ID}-${submenuText}-${securityPillLabel}`
    );
  };

  return (
    <div className={cx} ref={ref}>
      <SubmenuDesktop
        menuItemId={MARKET_DATA_MENU_ID}
        submenuItem={marketDataNews}
        trackingFunction={menuItemTrackingFunction}
      />
      <SubmenuDesktop
        menuItemId={MARKET_DATA_MENU_ID}
        submenuItem={marketDataLinks}
        trackingFunction={menuItemTrackingFunction}
      />
      <div className={styles.marketData}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.tickersAndWatchlistButton}>
          <div className={styles.tickers}>
            {!pricingData &&
              [...new Array(TICKER_COUNT)].map((i) => (
                <div key={i} className={styles.loadingPill} />
              ))}
            {pricingData && pricingData.length
              ? pricingData.map((data) => (
                  <a
                    key={data.id}
                    className={styles.tickerLink}
                    href={localizeUrl(locale, `/quote/${data.id}`)}
                    aria-label={data.shortName}
                    onClick={() =>
                      marketDataTrackingFunction(title, data.label || data.id)
                    }
                  >
                    <SecurityPill
                      ticker={data.id}
                      displayName={data.label || data.id}
                      percentChange={data.percentChange1Day}
                      price={
                        Math.round(Number(data.price.replace(/,/g, '')) * 100) /
                        100
                      }
                      variant={Variant.refresh2024}
                      lines={Lines.three}
                      size={Size.default}
                    />
                  </a>
                ))
              : ''}
          </div>
          <div className={styles.addToWatchlist}>
            <h3 className={styles.addToWatchlistLabel}>
              <TranslatedMessage
                id="media-ui-nav_trackYourPortFolionFromAnywhere"
                defaultMessage="Track your portfolio from anywhere with a Bloomberg Watchlist."
              />
            </h3>
            <a
              className={styles.addToWatchlistButton}
              aria-label={translateMessage({
                id: 'media-ui-nav_arialabel_addToWatchlist',
                defaultMessage: 'Add To Watchlist',
              })}
              role="button"
              href="/markets/watchlist"
              onClick={() => trackingFunction('click', 'Create Watchlist')}
            >
              <PlusStroke />
              <TranslatedMessage
                id="media-ui-nav_createWatchlist"
                defaultMessage="Create Watchlist"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
});
